import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extensions } from '@/api/extensions';
import type { ExtensionRow, CreateExtensionBody } from '@/api/extensions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, PhoneCall, KeyRound, Copy, UserSquare } from 'lucide-react';
import { ContactPicker } from '@/components/ContactPicker';
import { contacts as contactsApi } from '@/api/contacts';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function ExtensionsList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = CAN_MUTATE.includes(user.role);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExtensionRow | null>(null);
  const [regenTarget, setRegenTarget] = useState<ExtensionRow | null>(null);
  const [cidTarget, setCidTarget] = useState<ExtensionRow | null>(null);
  const [revealedPassword, setRevealedPassword] = useState<{ number: string; password: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['extensions', search, effectiveCustomerId],
    queryFn: () => extensions.list({
      search: search.trim() || undefined,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => extensions.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['extensions'] }); setDeleteTarget(null); },
  });
  const regen = useMutation({
    mutationFn: (ext: ExtensionRow) => extensions.regeneratePassword(ext.id),
    onSuccess: (v, ext) => {
      setRevealedPassword({ number: ext.number, password: v.password });
      setRegenTarget(null);
    },
  });

  const columns = [
    { header: 'Ext.', cell: (r: ExtensionRow) => <span className="font-mono text-sm">{r.number}</span> },
    { header: 'Name', cell: (r: ExtensionRow) => (
      r.display_name ? <span className="text-sm">{r.display_name}</span>
      : <span className="text-xs text-slate-400">—</span>
    ) },
    { header: 'SIP user', cell: (r: ExtensionRow) => <span className="font-mono text-xs text-slate-500">{r.sip_username}</span> },
    { header: 'Internal CID', cell: (r: ExtensionRow) => (
      <CidCell row={r} />
    ) },
    { header: 'Active', cell: (r: ExtensionRow) => (
      r.active ? <Badge variant="default">Active</Badge> : <Badge variant="secondary">Inactive</Badge>
    ) },
    { header: '', className: 'w-10 text-end', cell: (r: ExtensionRow) => (
      canMutate ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Row actions" onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => setCidTarget(r)}>
              <UserSquare className="h-3.5 w-3.5 mr-2" /> Set internal CID
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setRegenTarget(r)}>
              <KeyRound className="h-3.5 w-3.5 mr-2" /> Regenerate SIP password
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              onClick={() => setDeleteTarget(r)}>
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Telephony"
        title="Extensions"
        description="SIP extensions for softphones, desk phones, and ring groups. Each extension has a SIP username + password callers register against the FreeSWITCH internal profile on port 5060."
        actions={
          <div className="flex items-center gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ext or name..." className="h-9 w-48 text-sm" />
            {canMutate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" /> New extension
              </Button>
            )}
          </div>
        }
      />

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <DataTable rows={rows} columns={columns} empty={
          search ? `No extensions match "${search}".` : (
            <div className="py-10 text-center">
              <PhoneCall className="mx-auto h-8 w-8 text-slate-300 mb-3" />
              <p className="text-sm text-muted-foreground">No extensions yet.</p>
              {canMutate && (
                <Button size="sm" className="mt-3" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Create the first extension
                </Button>
              )}
            </div>
          )
        } />
      )}

      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} user={user} onCreated={(row) => {
        if (row.sip_password) setRevealedPassword({ number: row.number, password: row.sip_password });
      }} />}
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Delete extension?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              Ext. <span className="font-mono">{deleteTarget.number}</span> will be soft-deleted.
              Softphones registered to this extension will fail to authenticate on next re-register.
            </p>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" disabled={remove.isPending}>Cancel</Button></DialogClose>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget.id)}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {regenTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setRegenTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Regenerate SIP password?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              A new random password will be generated for ext. <span className="font-mono">{regenTarget.number}</span>.
              The existing one will stop working immediately — softphones will need to be reconfigured.
            </p>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" disabled={regen.isPending}>Cancel</Button></DialogClose>
              <Button disabled={regen.isPending} onClick={() => regen.mutate(regenTarget)}>
                {regen.isPending ? 'Regenerating…' : 'Regenerate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {revealedPassword && <RevealDialog payload={revealedPassword} onClose={() => setRevealedPassword(null)} />}
      {cidTarget && (
        <SetCidDialog
          ext={cidTarget}
          onClose={() => setCidTarget(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['extensions'] });
            setCidTarget(null);
          }}
        />
      )}
    </>
  );
}

/**
 * Inline cell showing the resolved internal CID — contact's display name
 * (or override) — so an admin can see at a glance which extensions are
 * configured and which aren't. Cached via the same contacts.picker
 * query the ContactPicker uses, so the cell is hydrated for free.
 */
function CidCell({ row }: { row: ExtensionRow }) {
  const customerId = row.customer_id;
  const { data } = useQuery({
    queryKey: ['contacts', 'picker', customerId],
    queryFn: () => contactsApi.list({ customer_id: customerId, active_only: true, limit: 500 }),
    enabled: !!row.internal_cid_contact_id,
  });
  if (row.internal_cid_contact_id) {
    const contact = data?.rows.find((c) => c.id === row.internal_cid_contact_id);
    if (contact) {
      return (
        <span className="text-xs">
          <span className="font-medium">{contact.display_name}</span>
          {contact.internal_cid_phone && (
            <span className="font-mono text-muted-foreground"> · {contact.internal_cid_phone}</span>
          )}
        </span>
      );
    }
    return <span className="text-xs text-slate-400">…</span>;
  }
  if (row.internal_cid_override) {
    return <span className="text-xs italic">{row.internal_cid_override}</span>;
  }
  return <span className="text-xs text-slate-400">—</span>;
}

/**
 * Quick dialog for setting (or clearing) an extension's internal CID.
 * Two options: pick a contact OR type a freeform override. The picker
 * wins if both are set — same precedence the engine uses at bridge time.
 */
function SetCidDialog({
  ext, onClose, onSaved,
}: {
  ext: ExtensionRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [contactId, setContactId] = useState<string | null>(ext.internal_cid_contact_id);
  const [override, setOverride] = useState(ext.internal_cid_override ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => extensions.update(ext.id, {
      internal_cid_contact_id: contactId,
      internal_cid_override: override.trim() || null,
    }),
    onSuccess: () => onSaved(),
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !save.isPending) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserSquare className="h-4 w-4" />
            Internal CID for ext. {ext.number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-muted-foreground leading-snug">
            What the called party sees on their handset when someone dials
            this extension internally. Pick a contact (preferred) or type
            a freeform name. If both are blank, the system falls back to
            the original caller's external CID.
          </p>
          <ContactPicker
            label="Linked contact"
            value={contactId}
            onChange={setContactId}
          />
          <div className="space-y-1.5">
            <Label htmlFor="cid-override">Freeform override</Label>
            <Input
              id="cid-override"
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="e.g. Reception, Sales"
              maxLength={200}
            />
            <p className="text-[11px] text-muted-foreground">
              Used only when no contact is linked above.
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={save.isPending}>Cancel</Button>
          </DialogClose>
          <Button onClick={() => { setError(null); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  onClose, user, onCreated,
}: {
  onClose: () => void;
  user: ReturnType<typeof useCurrentUser>;
  onCreated: (row: ExtensionRow & { sip_password?: string }) => void;
}) {
  const qc = useQueryClient();
  const [number, setNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [customPassword, setCustomPassword] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateExtensionBody) => extensions.create(body),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['extensions'] });
      onCreated(row);
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  function submit() {
    setError(null);
    if (!/^[0-9]{2,10}$/.test(number)) { setError('Extension number must be 2–10 digits.'); return; }
    if (effectiveCustomerId === null) { setError('Customer required.'); return; }
    create.mutate({
      number, display_name: displayName.trim() || null,
      sip_password: customPassword.trim() || undefined,
      ...(effectiveCustomerId
        ? { customer_id: effectiveCustomerId }
        : {}),
    });
  }

  const canSubmit = /^[0-9]{2,10}$/.test(number) && effectiveCustomerId !== null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New extension</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="e-number">Extension number</Label>
              <Input id="e-number" value={number} onChange={(e) => setNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="font-mono" placeholder="1001" />
              <p className="text-[11px] text-muted-foreground">2–10 digits, unique per customer.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="e-name">Display name</Label>
              <Input id="e-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Front desk" />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-password">SIP password (optional)</Label>
            <Input id="e-password" type="password" value={customPassword}
              onChange={(e) => setCustomPassword(e.target.value)}
              placeholder="Leave blank to generate a random one" />
            <p className="text-[11px] text-muted-foreground">
              Shown once on create. If left blank, we generate a 16-char random password.
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button></DialogClose>
          <Button type="button" onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending ? 'Creating…' : 'Create extension'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealDialog({ payload, onClose }: { payload: { number: string; password: string }; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(payload.password).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>SIP password — save this now</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <p>
            Password for extension <span className="font-mono font-semibold">{payload.number}</span>.
            We will never show it again — copy it into your softphone or password manager now.
          </p>
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
            <code className="font-mono text-sm flex-1 break-all">{payload.password}</code>
            <Button size="sm" variant="outline" onClick={copy}>
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
